import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { KeyHandlerService } from "@tylertech/core-module";
import { ODataFilterBuilder } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { IMetadataAction, IMetadataChipAction } from "@tylertech/tyler-api-types";
import { RepositoryFormData } from "../../interfaces";
import { CodeLookupService, ElementTheme, MetadataActionService, MetadataEventService, MetadataService } from "../../services";

export interface ActionCallBackArgs
{
    action: IMetadataAction;
    result: any;
}
@Component({
    selector: "metadata-action",
    templateUrl: "./metadataAction.tmpl.html",
    standalone: false
})
export class MetadataActionComponent implements OnInit, OnDestroy
{
    @Input() action: IMetadataAction;
    @Input() model: any;
    @Input() formData: RepositoryFormData;
    @Input() doFetch: boolean;
    @Input() dense: boolean;
    @Input() primary: boolean;
    @Input() enableShortcut: boolean;
    @Input() removeTabFocus: boolean = false;
    @Input() expansionPanelAction: boolean;
    // @Output() private readonly actionCallback?: (args: { action: IMetadataAction, result: any; }) => void;
    @Output() readonly executed = new EventEmitter<void>();
    @Output() private readonly actionCallback? = new EventEmitter<ActionCallBackArgs>();
    @Input() private readonly fetchEntity?: () => Promise<any>;

    modelToUse: any;
    keyHandlerId: string;
    _theme: string;

    private readonly destructor$ = new Subject<void>();

    constructor(
        private readonly $codeLookupService: CodeLookupService,
        private readonly $metadataService: MetadataService,
        private readonly $metadataActionService: MetadataActionService,
        private $metadataEventService: MetadataEventService,
        private readonly $keyHandlerService: KeyHandlerService,
    ) { }

    ngOnInit(): void
    {
        const self = this;
        this.keyHandlerId = Utilities.generateRandomGuid();
        if (!Utilities.IsNullOrUndefined(this.action.LookupForLabel))
        {
            this.$metadataEventService.onLookupTypeCacheClear.pipe(takeUntil(this.destructor$))
                .subscribe((type) =>
                {
                    if (type === this.action.LookupForLabel)
                    {
                        self._lookupForLabelIsLoaded = false;
                        self._labelWithLookupResult = null;
                    }
                });
        }

        const action = this.action as IMetadataChipAction;
        this._theme = !Utilities.IsNullOrUndefined(action.ThemeProperty)
            ? action.ThemeProperty && this.$metadataService.DependentModuleValue(action.ThemeProperty, null, this.model)
            : action.Theme;

        switch (this.theme)
        {
            case ElementTheme.Error:
                this.action.Icon = "error_outline";
                break;

            case ElementTheme.Warning:
                this.action.Icon = "warning";
                break;

            case ElementTheme.Success:
                this.action.Icon = "check_circle_outline";
                break;

            case ElementTheme.Default:
                this.action.Icon = !Utilities.IsNullOrUndefined(action.ThemeProperty) ? "" : this.action.Icon;
                break;
        }
    }

    ngOnDestroy()
    {
        if (this.enableShortcut)
            this.$keyHandlerService.unsubscribeAllFromClass(this.keyHandlerId);
        this.destructor$.next();
        this.destructor$.complete();
    }

    loadModelToUse(): void
    {
        if (Utilities.IsNullOrUndefinedOrEmptyObject(this.formData) || Utilities.IsNullOrUndefinedOrEmptyObject(this.formData.OtherEntities))
        {
            this.modelToUse = this.model;
        }
        else
        {
            const pattern = /^OtherEntities\.([\w.]*)(?:\[(\d+)\])?\.?([\w.]*)?/;
            const matches = pattern.exec(this.action.ModelPath);
            if (!Utilities.IsNullOrUndefined(matches))
            {
                const type = matches[1];
                const index = matches[2];
                const child = matches[3];
                this.modelToUse = index
                    ? child
                        ? this.formData.OtherEntities[type][index][child]
                        : this.formData.OtherEntities[type][index]
                    : this.formData.OtherEntities[type];
            }
            else
            {
                this.modelToUse = this.model;
            }
        }
    }

    get buttonType(): string
    {
        return this.primary ? "outlined" : "";
    }

    get hasIcon(): boolean
    {
        return !!this.action.Icon;
    }

    get isChip(): boolean
    {
        const action = this.action as IMetadataChipAction;
        return action.IsChip;
    }

    get theme(): string
    {
        return this._theme;
    }

    get Icon(): string
    {
        return this.$metadataService.EvaluateString(this.action.Icon, null, this.model);
    }

    private _lookupForLabelIsLoaded: boolean;
    private _labelWithLookupResult: string;
    get label(): string
    {
        if (this.labelUsesLookup)
        {
            this.LoadLookupForLabel();
            return this._labelWithLookupResult ?? this.EvaluateStringWithExplicitValue(this.action.Label, "…");
        }
        if (!Utilities.IsNullOrUndefined(this.action.Label))
            return this.$metadataService.EvaluateString(this.action.Label, null, this.modelToUse);
        return this.action.Label;
    }

    get telemetry(): string
    {
        return `Submission-${this.label}`;
    }

    get labelUsesLookup(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.action.Label) && !Utilities.IsNullOrUndefined(this.action.LookupForLabel);
    }

    private LoadLookupForLabel(): void
    {
        const self = this;
        if (!this._lookupForLabelIsLoaded && !Utilities.IsNullOrUndefined(this.modelToUse))
        {
            this._lookupForLabelIsLoaded = true;
            this.EvaluateStringWithLookup(this.action.Label, null, this.modelToUse, this.action.LookupForLabel)
                .then(v => self._labelWithLookupResult = v);
        }
    }

    private _disabled: boolean;
    get Disabled(): boolean
    {
        this.loadModelToUse();
        const disabled = this.$metadataActionService.IsDisabled(this.action, this.modelToUse) ?? false;
        if (this._disabled !== disabled)
        {
            this.registerKeyboardShortcut(disabled);
            this._disabled = disabled;
        }
        return disabled;
    }

    get tabindex(): string
    {
        return this.removeTabFocus ? "-1" : "0";
    }

    execute($event?: MouseEvent): void
    {
        $event?.stopPropagation();

        this.executed.emit();

        // TODO - why are we explicitly testing for `true`? The method definition says it only returns `boolean`
        if (this.$metadataActionService.OpenNewTabIfNeeded(this.model, this.action) === true)
        {
            return;
        }

        (this.doFetch
            ? this.fetchEntity()
            : Promise.resolve(this.model))
            .then(model =>
            {
                return this.$metadataActionService.ExecuteAction(model, this.action, this.formData);
            })
            .then(result =>
            {
                // result may be undefined if the action doesn't return anything,
                // but want to call actionCallback if available, since the action
                // is done successfully
                if (!Utilities.IsNullOrUndefined(this.actionCallback))
                {
                    this.actionCallback.emit({ action: this.action, result: result });
                }
            });
    }

    registerKeyboardShortcut(disabled: boolean): void
    {
        if (this.enableShortcut && this.action?.ShortcutMetadata)
        {
            if (disabled)
            {
                if (this.action.ShortcutMetadata)
                    this.$keyHandlerService.unsubscribe(this.action.ShortcutMetadata.Key, this.action.ShortcutMetadata.ModifierKey);
            }

            else
                this.$keyHandlerService.registerShortcutMetadata(this.keyHandlerId, this.action.ShortcutMetadata, `Action|${this.action.Label}`, () => this.execute(null));
        }
    }

    /**
     * Evaluates the given metadata value and replaces any variables with the given value.
     * Intended to be used while loading, before the model is available.
     * @param metadataValue metadata value describing desired value
     * @param replacementToUse value to replace all variables with.
     */
    private EvaluateStringWithExplicitValue(metadataValue: string, replacementToUse: string): string
    {
        let regexMatch = metadataValue.match("{[^}]*}");
        while (regexMatch !== null)
        {
            metadataValue = metadataValue.replace(/{[^}]*}/, replacementToUse);
            regexMatch = metadataValue.match("{[^}]*}");
        }
        return metadataValue;
    }

    /**
     * Evaluates the given metadata value against the given parent/model pair, then calls a lookup with that value and returns the lookup result.
     * @param metadataValue metadata value describing desired value
     * @param parent object that the model belongs to.
     * @param model model the that metadata is describing.
     * @param lookup lookup to be used for the replacement.
     */
    private EvaluateStringWithLookup(metadataValue: string, parent: any, model: any, lookup: string, formData?: RepositoryFormData, urlEncodeValue?: boolean): Promise<string>
    {
        let regexMatch = metadataValue.match("{[^}]*}");
        if (regexMatch === null)
        {
            return Promise.resolve(metadataValue);
        }

        let moduleValue = this.$metadataService.ReplaceMatch(regexMatch, parent, model, formData, urlEncodeValue, metadataValue);
        const builder = new ODataFilterBuilder()
            .filterEQ("Key", moduleValue);
        return this.$codeLookupService.Get(lookup, builder.build())
            .then(
                x =>
                {
                    if (x && x.length > 0)
                    {
                        moduleValue = x[0].Value;
                    }

                    metadataValue = metadataValue.replace(/{[^}]*}/, moduleValue);
                    regexMatch = metadataValue.match("{[^}]*}");
                    return this.EvaluateStringWithLookup(metadataValue, parent, model, lookup, formData, urlEncodeValue);
                },
                () =>
                {
                    return "Error Loading";
                }
            );
    }
}
